import type { HomeMemory, HomeResponse } from '../../../api/types';
import LineChart from './LineChart';

interface HomeScreenProps {
  data?: HomeResponse | null;
  loading?: boolean;
  error?: string | null;
  onOpenAnalytics?: () => void;
  onOpenCalendar?: () => void;
  onCreateMemory?: () => void;
  onOpenMemory?: (memoryId: string) => void;
}

const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS_RU = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const EMOTION_COLOR: Record<string, string> = {
  joy: '#3ECFA0',
  sadness: '#4A90E2',
  fear: '#8B7AB8',
  anger: '#FF6B6B',
};

function getFirstDayOffset(year: number, month: number): number {
  const day = new Date(year, month - 1, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function formatShortDate(value: string): string {
  const date = new Date(value.includes('T') ? value : `${value}T00:00:00`);
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
}

function chartLabel(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU', {
    weekday: 'short',
  }).slice(0, 2);
}

function previewText(memory: HomeMemory): string {
  const text = memory.story_text?.trim();
  if (!text) return 'Воспоминание обрабатывается...';
  return text.length > 110 ? `${text.slice(0, 110)}...` : text;
}

export default function HomeScreen({
  data,
  loading = false,
  error = null,
  onOpenAnalytics,
  onOpenCalendar,
  onCreateMemory,
  onOpenMemory,
}: HomeScreenProps) {
  const calendar = data?.month_calendar;
  const calendarYear = calendar?.year ?? new Date().getFullYear();
  const calendarMonth = calendar?.month ?? new Date().getMonth() + 1;
  const startOffset = getFirstDayOffset(calendarYear, calendarMonth);
  const daysInMonth = getDaysInMonth(calendarYear, calendarMonth);
  const todayIso = new Date().toISOString().slice(0, 10);
  const dayMap = new Map(calendar?.days.map((day) => [day.date, day]) ?? []);
  const chartData =
    data?.last_7_days.map((point) => ({
      joy: point.joy,
      sadness: point.sadness,
      fear: point.fear,
      anger: point.anger,
      label: chartLabel(point.date),
    })) ?? [];
  const hasChartValues = chartData.some((point) => point.joy || point.sadness || point.fear || point.anger);
  const recent = data?.recent_memories ?? [];

  return (
    <div
      className="min-h-screen pb-28"
      style={{ background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)' }}
    >
      {/* Top nav */}
      <div
        className="sticky top-0 z-10 flex items-center justify-between px-5"
        style={{
          height: '56px',
          background: 'rgba(26,27,46,0.95)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div style={{ width: '28px' }} />
        <span style={{ fontSize: '18px', fontWeight: 600, color: '#F5F5F7', fontFamily: 'system-ui' }}>Главная</span>
        <div className="w-7 h-7 flex items-center justify-center">
          <i className="ri-notification-3-line" style={{ color: '#6B7A94', fontSize: '20px' }} />
        </div>
      </div>

      <div className="px-4 pt-4" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && (
          <div
            style={{
              background: 'rgba(255,107,107,0.12)',
              border: '1px solid rgba(255,107,107,0.25)',
              borderRadius: '16px',
              padding: '14px 16px',
              color: '#FFD1D1',
              fontSize: '13px',
              lineHeight: 1.5,
            }}
          >
            Не удалось загрузить данные: {error}
          </div>
        )}

        {/* Add memory banner */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #3ECFA0 0%, #2BA87F 100%)',
            padding: '20px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            cursor: 'pointer',
          }}
          onClick={onCreateMemory}
        >
          <div
            style={{
              width: '52px', height: '52px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className="ri-mic-fill text-2xl text-white" />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '18px', fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>Добавить воспоминание</p>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '3px' }}>Нажмите, чтобы начать</p>
          </div>
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-2xl text-white" />
          </div>
        </div>

        {/* 7-day emotion chart */}
        <div
          className="rounded-3xl"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            padding: '18px 18px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <span style={{ fontSize: '17px', fontWeight: 600, color: '#F5F5F7' }}>Последние 7 дней</span>
            <button
              onClick={onOpenAnalytics}
              style={{ fontSize: '13px', fontWeight: 500, color: '#3ECFA0', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Подробнее
            </button>
          </div>
          {loading && !data ? (
            <ChartSkeleton />
          ) : hasChartValues ? (
            <LineChart days={7} height={130} showLegend data={chartData} />
          ) : (
            <EmptyPanel icon="ri-line-chart-line" text="Здесь появится динамика эмоций после первых воспоминаний." />
          )}
        </div>

        {/* Calendar */}
        <div
          className="rounded-3xl"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            padding: '18px 18px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <span style={{ fontSize: '17px', fontWeight: 600, color: '#F5F5F7' }}>
              {MONTHS_RU[calendarMonth - 1]} {calendarYear}
            </span>
            <button onClick={onOpenCalendar} style={{ fontSize: '13px', fontWeight: 500, color: '#3ECFA0', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Перейти в календарь
            </button>
          </div>

          {/* Week headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '8px' }}>
            {weekDays.map((d) => (
              <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 500, color: '#6B7A94', padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {Array.from({ length: startOffset }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateIso = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayData = dayMap.get(dateIso);
              const color = dayData?.dominant_emotion ? EMOTION_COLOR[dayData.dominant_emotion] : null;
              const isToday = dateIso === todayIso;
              return (
                <div
                  key={day}
                  style={{
                    aspectRatio: '1',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    background: color ?? 'transparent',
                    border: color ? 'none' : isToday ? '2px solid #3ECFA0' : '1.5px solid rgba(255,255,255,0.15)',
                    boxShadow: isToday && !color ? '0 0 0 2px rgba(62,207,160,0.3)' : 'none',
                    fontSize: '12px',
                    fontWeight: isToday ? 600 : 400,
                    color: color ? '#fff' : '#A8B2C8',
                  }}
                >
                  {day}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '14px', justifyContent: 'center' }}>
            {[
              { color: '#3ECFA0', label: 'Радость' },
              { color: '#4A90E2', label: 'Печаль' },
              { color: '#8B7AB8', label: 'Страх' },
              { color: '#FF6B6B', label: 'Гнев' },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.color }} />
                <span style={{ fontSize: '11px', color: '#A8B2C8' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent memories */}
        <div style={{ paddingBottom: '8px' }}>
          <p style={{ fontSize: '17px', fontWeight: 600, color: '#F5F5F7', marginBottom: '12px' }}>Недавние воспоминания</p>
          {loading && !data ? (
            <RecentSkeleton />
          ) : recent.length > 0 ? (
            recent.map((memory) => {
              const color = memory.dominant_emotion ? EMOTION_COLOR[memory.dominant_emotion] : '#6B7A94';
              return (
                <div
                  key={memory.id}
                  className="rounded-2xl mb-3"
                  onClick={() => onOpenMemory?.(memory.id)}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    padding: '14px 16px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{memory.title}</span>
                    </div>
                    <span style={{ fontSize: '12px', color: '#6B7A94', flexShrink: 0, marginLeft: '8px' }}>{formatShortDate(memory.recorded_at)}</span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#A8B2C8', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {previewText(memory)}
                  </p>
                  <p style={{ fontSize: '11px', color: '#6B7A94', marginTop: '8px' }}>
                    Событие: {formatShortDate(memory.event_date)}
                  </p>
                </div>
              );
            })
          ) : (
            <EmptyPanel icon="ri-book-open-line" text="Пока нет сохранённых воспоминаний. Начните с первой записи." />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyPanel({ icon, text }: { icon: string; text: string }) {
  return (
    <div
      style={{
        minHeight: '112px',
        borderRadius: '18px',
        border: '1px dashed rgba(255,255,255,0.12)',
        background: 'rgba(255,255,255,0.035)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '18px',
        textAlign: 'center',
      }}
    >
      <i className={icon} style={{ fontSize: '22px', color: '#3ECFA0' }} />
      <p style={{ fontSize: '13px', color: '#A8B2C8', lineHeight: 1.5, maxWidth: '250px' }}>{text}</p>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div style={{ height: '168px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: '13px', color: '#6B7A94' }}>Загружаем аналитику...</span>
    </div>
  );
}

function RecentSkeleton() {
  return (
    <>
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="rounded-2xl mb-3"
          style={{
            height: '76px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
          }}
        />
      ))}
    </>
  );
}
